import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import type { B2BBuyer, B2BBuyerFormData } from '../types/b2b';
import { hashPassword } from '../lib/auth';
import { getAdminId } from '../utils/getAdminId';

function formatBuyerCode(num: number): string {
  return `B2B-${String(num).padStart(3, '0')}`;
}

function parseBuyerCodeSuffix(code: string): number {
  const match = /^B2B-(\d+)$/i.exec(code.trim());
  if (!match) return 0;
  const num = Number.parseInt(match[1], 10);
  return Number.isFinite(num) ? num : 0;
}

function isUniqueViolation(error: { code?: string; message?: string; details?: string }, constraint: string): boolean {
  if (error.code !== '23505') return false;
  const haystack = `${error.message ?? ''} ${error.details ?? ''}`.toLowerCase();
  return haystack.includes(constraint.toLowerCase());
}

export const useB2BBuyers = () => {
  const { user } = useAuth();
  const [buyers, setBuyers] = useState<B2BBuyer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBuyers = useCallback(async () => {
    if (!user || user.role !== 'admin') return;
    const adminId = getAdminId(user);
    if (!adminId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('b2b_buyers')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setBuyers((data ?? []).map(b => ({ ...b, password_hash: undefined })));
    } catch (err: any) {
      setError(err.message || 'Failed to load buyers');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadBuyers();
  }, [loadBuyers]);

  const generateBuyerCode = useCallback(async (): Promise<string> => {
    const adminId = getAdminId(user);
    if (!adminId) return formatBuyerCode(1);

    const { data, error: fetchError } = await supabase
      .from('b2b_buyers')
      .select('buyer_code')
      .eq('admin_id', adminId);

    if (fetchError) throw fetchError;

    let maxSuffix = 0;
    for (const row of data ?? []) {
      const suffix = parseBuyerCodeSuffix(row.buyer_code ?? '');
      if (suffix > maxSuffix) maxSuffix = suffix;
    }

    return formatBuyerCode(maxSuffix + 1);
  }, [user]);

  const createBuyer = useCallback(async (formData: B2BBuyerFormData): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Not authenticated' };
    const adminId = getAdminId(user);
    if (!adminId) return { success: false, error: 'No active company selected' };
    try {
      const passwordHash = await hashPassword(formData.password);
      const email = formData.email.trim().toLowerCase();

      const buildPayload = (buyerCode: string) => ({
        admin_id: adminId,
        buyer_code: buyerCode,
        email,
        password_hash: passwordHash,
        legal_entity_name: formData.legal_entity_name,
        trade_name: formData.trade_name,
        vat_number: formData.vat_number || null,
        company_registration_number: formData.company_registration_number || null,
        billing_address: formData.billing_address || null,
        billing_city: formData.billing_city || null,
        billing_postal_code: formData.billing_postal_code || null,
        billing_country: formData.billing_country || null,
        shipping_address: formData.shipping_address || null,
        shipping_city: formData.shipping_city || null,
        shipping_postal_code: formData.shipping_postal_code || null,
        shipping_country: formData.shipping_country || null,
        contact_person_name: formData.contact_person_name,
        contact_person_role: formData.contact_person_role || null,
        phone: formData.phone || null,
        email_contact: formData.email_contact || null,
        alternative_contact_name: formData.alternative_contact_name || null,
        alternative_contact_email: formData.alternative_contact_email || null,
        alternative_contact_phone: formData.alternative_contact_phone || null,
        preferred_language: formData.preferred_language || 'en',
        preferred_currency: formData.preferred_currency || 'EUR',
        payment_terms: formData.payment_terms || null,
        credit_limit: formData.credit_limit || 0,
        account_discount: formData.account_discount || 1,
        vat_percentage: formData.vat_percentage ?? 21,
        internal_notes: formData.internal_notes || null,
        account_status: formData.account_status || 'pending_approval',
        is_active: formData.account_status === 'active',
      });

      let buyerCode = await generateBuyerCode();
      let { error: insertError } = await supabase
        .from('b2b_buyers')
        .insert(buildPayload(buyerCode));

      const isBuyerCodeConflict = (err: { code?: string; message?: string; details?: string } | null) =>
        !!err && (
          isUniqueViolation(err, 'b2b_buyers_buyer_code_key')
          || isUniqueViolation(err, 'b2b_buyers_admin_id_buyer_code_key')
          || (err.code === '23505' && /buyer_code/i.test(`${err.message ?? ''} ${err.details ?? ''}`))
        );

      if (isBuyerCodeConflict(insertError)) {
        buyerCode = await generateBuyerCode();
        ({ error: insertError } = await supabase
          .from('b2b_buyers')
          .insert(buildPayload(buyerCode)));
      }

      if (insertError) {
        if (isUniqueViolation(insertError, 'b2b_buyers_email_key')
          || (insertError.code === '23505' && /email/i.test(`${insertError.message ?? ''} ${insertError.details ?? ''}`))) {
          return { success: false, error: 'A buyer with this email already exists.' };
        }
        if (isBuyerCodeConflict(insertError)) {
          return { success: false, error: 'Could not allocate a unique buyer code. Please try again.' };
        }
        if (insertError.code === '23505') {
          return { success: false, error: 'A duplicate record already exists.' };
        }
        throw insertError;
      }

      await loadBuyers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to create buyer' };
    }
  }, [user, generateBuyerCode, loadBuyers]);

  const updateBuyer = useCallback(async (buyerId: string, updates: Partial<B2BBuyer>): Promise<{ success: boolean; error?: string }> => {
    try {
      const updateData: Record<string, any> = { ...updates, updated_at: new Date().toISOString() };
      delete updateData.id;
      delete updateData.admin_id;
      delete updateData.buyer_code;

      if (typeof updateData.email === 'string') {
        updateData.email = updateData.email.trim().toLowerCase();
      }

      if (updateData.account_status) {
        updateData.is_active = updateData.account_status === 'active';
      }

      const { error: updateError } = await supabase
        .from('b2b_buyers')
        .update(updateData)
        .eq('id', buyerId);

      if (updateError) throw updateError;
      await loadBuyers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update buyer' };
    }
  }, [loadBuyers]);

  const updateBuyerStatus = useCallback(async (buyerId: string, status: B2BBuyer['account_status']): Promise<{ success: boolean; error?: string }> => {
    return updateBuyer(buyerId, { account_status: status, is_active: status === 'active' } as Partial<B2BBuyer>);
  }, [updateBuyer]);

  const deleteBuyer = useCallback(async (buyerId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error: deleteError } = await supabase
        .from('b2b_buyers')
        .delete()
        .eq('id', buyerId);

      if (deleteError) throw deleteError;
      await loadBuyers();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to delete buyer' };
    }
  }, [loadBuyers]);

  return {
    buyers,
    loading,
    error,
    loadBuyers,
    createBuyer,
    updateBuyer,
    updateBuyerStatus,
    deleteBuyer,
  };
};
